import React, { useMemo } from 'react';
import * as THREE from 'three';
import { useWorldTime, localHour } from '../worldTime';
import { SLICE_DISTRICT } from '../config';

// SkyDome — a textured sky that changes with the day schedule. Four Gemini
// equirectangular skies (dawn/day/dusk/night, with stars + clouds) mapped onto
// a big inverted sphere; the two bracketing the current hour crossfade. Sits
// outside the skyline ring (r 300) and inside the camera far plane (1200).
// (A still today — the same panos can later drive a looping video sky.)

const R = 900;
// Shared with CityBox, whose walls carry the SAME sky so the backdrop behind
// the skyline is continuous with the dome above it.
export const PHASES = ['dawn', 'day', 'dusk', 'night'];

// Keyframe hours → which sky. Smooth crossfade between neighbours.
const STOPS = [
  { h: 0, k: 'night' }, { h: 5, k: 'night' }, { h: 6.5, k: 'dawn' }, { h: 9, k: 'day' },
  { h: 16.5, k: 'day' }, { h: 19.5, k: 'dusk' }, { h: 21.5, k: 'night' }, { h: 24, k: 'night' },
];
export function pickSkies(hour) {
  let i = 0;
  for (; i < STOPS.length - 1; i++) if (hour >= STOPS[i].h && hour < STOPS[i + 1].h) break;
  const a = STOPS[i], b = STOPS[i + 1] || STOPS[i];
  const span = (b.h - a.h) || 1;
  return { a: a.k, b: b.k, blend: Math.min(1, Math.max(0, (hour - a.h) / span)) };
}

export default function SkyDome() {
  useWorldTime(SLICE_DISTRICT.timezone); // re-render on the clock tick / ?time
  const tz = SLICE_DISTRICT.timezone;

  const tex = useMemo(() => {
    const loader = new THREE.TextureLoader();
    const out = {};
    for (const p of PHASES) {
      const t = loader.load(`/skybox/sky_${p}.jpg`);
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = THREE.RepeatWrapping;
      t.anisotropy = 4;
      out[p] = t;
    }
    return out;
  }, []);
  useMemo(() => () => Object.values(tex).forEach((t) => t.dispose()), [tex]);

  const { a, b, blend } = pickSkies(localHour(tz));

  return (
    <group renderOrder={-10}>
      {/* base sky (fully opaque) */}
      <mesh renderOrder={-11}>
        <sphereGeometry args={[R, 40, 24]} />
        <meshBasicMaterial map={tex[a]} side={THREE.BackSide} depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      {/* next sky, crossfaded in as the hour advances */}
      {blend > 0.001 && (
        <mesh renderOrder={-10}>
          <sphereGeometry args={[R - 2, 40, 24]} />
          <meshBasicMaterial map={tex[b]} side={THREE.BackSide} depthWrite={false} fog={false} toneMapped={false} transparent opacity={blend} />
        </mesh>
      )}
    </group>
  );
}
